import { useMemo, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { actionPlanMessages } from '../actionPlanConfig'
import {
    ActionPlanMessage,
    ActionPlanMessagesId,
} from '../actionPlanConfig.types'
import { ProductivityIntegrationsModule } from '@/core/modules/productivity-integrations/productivity-integrations.module'
import { IssuesModule } from '@/core/modules/issues'
import { AccountModule } from '@/core/modules/account/account.module'
import { IntegrationsStore } from '@/core/store/Integrations/integrations.store'
import { UsersModule } from '@/core/modules/users/users.module'
import { useRouting } from '@/core/hooks/useRouting'
import { scrollToElement } from '@/ui/utils/utils'
import { Analytics } from '@/core/services/mixpanel'

const useActionPlan = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { handleSecurityAwarenessTrainingClick } = useRouting()
    const allFdIssues = UsersModule.getFdIssuesAsIssues().filter(
        (issue) => issue.status === 'open'
    )
    const becIssues = UsersModule.getBecIssuesAsIssues().filter(
        (issue) => issue.status === 'open'
    )
    const issues = IssuesModule.getIssuesWithSubscription({
        disableFilters: true,
    })

    const { isMdrActive } = AccountModule.useStore()
    const hasAtLeastOneActiveIntegrationWithDisabledFdService =
        IntegrationsStore.hasAtLeastOneActiveIntegrationWithDisabledFdService()
    const isWizerIntegrationActive =
        ProductivityIntegrationsModule.isWizerIntegrationActive()

    const hasCriticalIssues = useMemo(
        () =>
            issues.some(
                (issue) =>
                    issue.severity === 'critical' && issue.status === 'open'
            ),
        [issues]
    )

    const numberOfGoogleActiveIntegrations =
        IntegrationsStore.getActiveIntegrationsByType('google').length

    const numberOfMicrosoftActiveIntegrations =
        IntegrationsStore.getActiveIntegrationsByType('microsoft').length

    const googleFDActiveIntegrations =
        IntegrationsStore.getActiveIntegrationsByType('google_fraud_defense')

    const microsoftFDActiveIntegrations =
        IntegrationsStore.getActiveIntegrationsByType('ms_fraud_defense')

    const numberOfActiveCloudApps =
        numberOfGoogleActiveIntegrations + numberOfMicrosoftActiveIntegrations

    const numberOfActiveFDIntegrations =
        googleFDActiveIntegrations.length + microsoftFDActiveIntegrations.length

    const isCloudAppsActive = numberOfActiveCloudApps > 0
    const isFDActive = numberOfActiveFDIntegrations > 0

    const hasLowPriorityIssues = useMemo(() => {
        return issues.some(
            (issue) =>
                issue.status === 'open' &&
                (issue.severity === 'moderate' ||
                    issue.severity === 'important')
        )
    }, [issues])

    const shouldShowUpdateRequired =
        (numberOfMicrosoftActiveIntegrations > 0 &&
            microsoftFDActiveIntegrations.length === 0) ||
        (numberOfGoogleActiveIntegrations > 0 &&
            googleFDActiveIntegrations.length === 0)

    const messages = useMemo(() => {
        const shouldDisplayMessage: Record<ActionPlanMessagesId, boolean> = {
            'suspected-ato': becIssues.length > 0,
            'suspected-incoming-email': allFdIssues.length > 0,
            'critical-issues': hasCriticalIssues,
            'activate-integrations': !isCloudAppsActive && !isFDActive,
            'update-integration-required': shouldShowUpdateRequired,
            'enable-fraud-defense':
                hasAtLeastOneActiveIntegrationWithDisabledFdService,
            'mdr-not-active': !isMdrActive,
            'low-priority-issues': hasLowPriorityIssues,
            'security-training': !isWizerIntegrationActive,
            'release-notes': true,
            'patch-tuesday': true,
        }

        const filteredMessages = actionPlanMessages.filter(
            (message) => shouldDisplayMessage[message.id]
        )

        return filteredMessages.sort((a, b) => b.priority - a.priority)
    }, [
        becIssues.length,
        allFdIssues.length,
        hasCriticalIssues,
        isCloudAppsActive,
        isFDActive,
        shouldShowUpdateRequired,
        hasAtLeastOneActiveIntegrationWithDisabledFdService,
        isMdrActive,
        hasLowPriorityIssues,
        isWizerIntegrationActive,
    ])

    const handleAction = (message: ActionPlanMessage) => {
        const { redirection, id, type, priority } = message

        Analytics.event('SEM::user_clicks_action_plan_message', {
            actionPlanMessageId: id,
            messageType: type,
            priority,
            position: messages.findIndex((displayed) => displayed.id === id),
            redirectionType: redirection?.type,
            redirectionTarget: redirection?.value,
        })

        if (id === 'security-training') {
            handleSecurityAwarenessTrainingClick()
            return
        }

        if (!redirection) return

        if (redirection.type === 'external') {
            window.open(redirection.value, '_blank')
        } else {
            navigate(redirection.value, {
                state: redirection.scrollTo
                    ? { scrollTo: redirection.scrollTo }
                    : undefined,
            })
        }
    }

    useEffect(() => {
        const state = location.state as { scrollTo?: string } | null
        if (state?.scrollTo) {
            const scrollTarget = state.scrollTo
            if (!scrollTarget) return

            requestAnimationFrame(() => {
                scrollToElement(scrollTarget)
                navigate(location.pathname, { replace: true, state: {} })
            })
        }
    }, [location, navigate])

    return { messages, handleAction }
}

export default useActionPlan
